import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { BookingForm } from "@/components/BookingForm";
import xrayAsset from "@/assets/diagnostic-xray.jpg.asset.json";
import labAsset from "@/assets/diagnostic-lab.jpg.asset.json";
export const heroImg = xrayAsset.url;
export const labImg = labAsset.url;
import {
  Activity,
  HeartPulse,
  Scan,
  Stethoscope,
  Waves,
  Brain,
  TestTube,
  Microscope,
  Baby,
  Eye,
  CheckCircle2,
  Clock,
  ShieldCheck,
  BadgeIndianRupee,
} from "lucide-react";

export const Route = createFileRoute("/diagnostics")({
  head: () => ({
    meta: [
      { title: "Diagnostics — Healic Multispeciality Hospital" },
      { name: "description", content: "ECG, ECHO, TMT, X-Ray, Ultrasound & full pathology under one roof. Accurate, affordable diagnostics by experienced technicians." },
      { property: "og:title", content: "Diagnostics — Healic Multispeciality Hospital" },
      { property: "og:url", content: "https://healic.in/diagnostics" },
      { property: "og:description", content: "Book your diagnostic test with Healic. Our team will contact you shortly." },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/diagnostics" }],
  }),
  component: DiagnosticsPage,
});

type Test = {
  name: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  category: "Cardiac" | "Radiology" | "Ultrasound" | "Pathology" | "Neuro";
};

const tests: Test[] = [
  { name: "ECG", desc: "Electrocardiogram to record heart's electrical activity in minutes.", icon: Activity, category: "Cardiac" },
  { name: "ECHO", desc: "2D Echocardiography for detailed imaging of heart chambers & valves.", icon: HeartPulse, category: "Cardiac" },
  { name: "TMT", desc: "Treadmill Stress Test to evaluate heart response under exertion.", icon: HeartPulse, category: "Cardiac" },
  { name: "Holter Monitoring", desc: "24-hour continuous heart rhythm recording for arrhythmia detection.", icon: Activity, category: "Cardiac" },

  { name: "Digital X-Ray", desc: "High-resolution digital radiography with instant reporting.", icon: Scan, category: "Radiology" },

  { name: "Ultrasound (USG)", desc: "Whole abdomen, KUB, pelvic and small-parts ultrasonography.", icon: Waves, category: "Ultrasound" },
  { name: "Obstetric & Anomaly Scan", desc: "Antenatal scans including NT, anomaly and growth scans.", icon: Baby, category: "Ultrasound" },

  { name: "Complete Pathology", desc: "CBC, LFT, KFT, lipid, thyroid, diabetes, vitamin & hormone panels.", icon: TestTube, category: "Pathology" },
  { name: "Histopathology & Biopsy", desc: "Tissue biopsies, FNAC and microscopic analysis by expert pathologists.", icon: Microscope, category: "Pathology" },
];

const categories: Test["category"][] = ["Cardiac", "Radiology", "Ultrasound", "Pathology"];

const features = [
  { icon: ShieldCheck, title: "NABL-grade Accuracy", desc: "Calibrated machines & trained technicians for trusted reports." },
  { icon: Clock, title: "Same-Day Reports", desc: "Most tests reported the same day, many within an hour." },
  { icon: BadgeIndianRupee, title: "Transparent Pricing", desc: "No hidden charges — quoted price is the final price." },
  { icon: Stethoscope, title: "Doctor Consultation", desc: "Free consultation with our specialists on report review." },
];

function DiagnosticsPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
        <div className="overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary-soft via-background to-muted shadow-card">
          <div className="grid items-center gap-8 p-8 md:grid-cols-[1.1fr_1fr] lg:gap-12 lg:p-12">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                <Stethoscope className="h-3.5 w-3.5" /> Diagnostic Centre
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                Accurate Diagnostics, Right When You Need Them
              </h1>
              <p className="mt-5 max-w-xl text-base text-muted-foreground lg:text-lg">
                ECG, ECHO, TMT, X-Ray, Ultrasound and full pathology — all under one roof with modern equipment and same-day reports.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <a
                    key={c}
                    href={`#${c.toLowerCase()}`}
                    className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary"
                  >
                    {c}
                  </a>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl shadow-card">
              <img src={heroImg} alt="Healic diagnostics" className="h-full w-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-card">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-sm font-bold text-foreground">{f.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lab precision band */}
      <section className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid items-center gap-8 overflow-hidden rounded-3xl border border-border bg-card shadow-card lg:grid-cols-2">
          <div className="aspect-[16/10] overflow-hidden lg:aspect-auto lg:h-full">
            <img src={labImg} alt="Pathology lab microscopy at Healic" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div className="p-6 lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Precision pathology</p>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Microscope-grade accuracy, every report
            </h2>
            <p className="mt-3 text-sm text-muted-foreground lg:text-base">
              Our in-house lab is run by certified pathologists using calibrated analyzers and digital microscopy — so every sample is examined with clinical precision and reported the same day.
            </p>
          </div>
        </div>
      </section>

      {/* Tests by category */}
      <section className="mx-auto max-w-7xl px-4 py-14 lg:px-8 lg:py-20">
        <header className="mb-10">
          <h2 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Tests We Offer
          </h2>
          <p className="mt-2 text-base text-muted-foreground">
            Comprehensive diagnostic services across cardiology, radiology, pathology and more.
          </p>
        </header>

        <div className="space-y-12">
          {categories.map((cat) => {
            const items = tests.filter((t) => t.category === cat);
            return (
              <div key={cat} id={cat.toLowerCase()} className="scroll-mt-24">
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-1.5 w-10 rounded-full bg-primary" />
                  <h3 className="font-display text-xl font-bold text-foreground sm:text-2xl">{cat}</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((t) => (
                    <div
                      key={t.name}
                      className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-card transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
                    >
                      <div className="flex items-start gap-3">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                          <t.icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-display text-base font-bold text-foreground">{t.name}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-accent">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Same-day report
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <BookingForm title="Book Your Diagnostic Test" />
    </PageShell>
  );
}
